import React, { useState, useEffect } from 'react';
import { X, Download, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { getAppVersion, applyUpdate } from '../utils/versionCheck';

const UpdateBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Check if user has dismissed the banner for this version
    const dismissedVersion = localStorage.getItem('update_banner_dismissed');
    const currentVersion = getAppVersion();
    const storedVersion = localStorage.getItem('app_version');
    
    // Determine if banner should show
    const shouldShow = storedVersion && 
                       storedVersion !== currentVersion && 
                       dismissedVersion !== currentVersion;
    
    if (shouldShow) {
      // Use setTimeout to avoid setState during render
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleUpdate = async () => {
    setIsUpdating(true);
    try {
      await applyUpdate();
      // Page will reload after update
    } catch (error) {
      console.error('Update failed:', error);
      setIsUpdating(false);
    }
  };

  const handleDismiss = () => {
    const currentVersion = getAppVersion();
    localStorage.setItem('update_banner_dismissed', currentVersion);
    setShowBanner(false);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-gradient-to-r from-teal-600 to-emerald-600 text-white px-4 py-2.5 shadow-lg animate-in slide-in-from-top duration-300">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 shrink-0 animate-pulse" />
          <p className="text-xs sm:text-sm font-medium truncate">
            <span className="hidden sm:inline">🎉 New version available! </span>
            <span className="sm:hidden">Update available! </span>
            <span className="text-teal-100">v{getAppVersion()}</span>
            <span className="hidden md:inline"> - Accounts module addition</span>
          </p>
        </div>
        
        <div className="flex items-center gap-2 shrink-0">
          <Button
            onClick={handleUpdate}
            disabled={isUpdating}
            size="sm"
            className="bg-white dark:bg-card text-teal-700 hover:bg-teal-50 h-7 sm:h-8 text-xs sm:text-sm px-2 sm:px-3 font-medium"
          >
            {isUpdating ? (
              <>
                <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 animate-bounce" />
                <span className="hidden sm:inline">Updating...</span>
                <span className="sm:hidden">...</span>
              </>
            ) : (
              <>
                <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                <span className="hidden sm:inline">Update Now</span>
                <span className="sm:hidden">Update</span>
              </>
            )}
          </Button>
          
          <button
            onClick={handleDismiss}
            className="p-1 hover:bg-white dark:bg-card/20 rounded-full transition-colors"
            aria-label="Dismiss update banner"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateBanner;
